import { NextPage } from 'next'
import Head from 'next/head'

import getContentful from '@lib/contentful'
import processModularPage, {
  ModularPageEntryI,
  ModularPageI,
} from '@lib/contentful/entries/pages/modular'

import Page from '@components/page'
import CenterModule from '@components/undock/modules/center'
import GridModule from '@components/undock/modules/grid'
import HeroModule from '@components/undock/modules/hero'
import SideBySideModule from '@components/undock/modules/sideBySide'
import TestimonialsModule from '@components/undock/modules/testimonials'
import UpsellModule from '@components/undock/modules/upsell'
import Unfurl from '@components/page/unfurl'

const modulesMap = {
  centerModule: CenterModule,
  gridModule: GridModule,
  hero: HeroModule,
  sideBySide: SideBySideModule,
  testimonialsModule: TestimonialsModule,
  upsellModule: UpsellModule,
}

interface ModularPagePropsI extends ModularPageI {}

const ModularPage: NextPage<ModularPagePropsI> = ({
  modules = [],
  pageDescription,
  pageImage,
  pageKeywords,
  pageTitle,
}) => {
  return (
    <Page shouldScroll>
      <Unfurl
        description={pageDescription}
        image={pageImage}
        keywords={pageKeywords}
        title={pageTitle}
      />

      {modules &&
        modules.map((module) => {
          const Module = modulesMap[module.type]

          return <Module key={module.id} {...(module as any)} />
        })}
    </Page>
  )
}

export const getStaticPaths = async () => {
  const client = await getContentful()
  const entries = await client.getEntries<ModularPageEntryI>({
    content_type: 'modularPage',
  })

  const paths = entries.items.map((entry) => `/l/${entry.fields.slug}`)

  return {
    paths,
    fallback: false,
  }
}

export const getStaticProps = async ({ params }) => {
  const { slug } = params
  const client = await getContentful()
  const entries = await client.getEntries<ModularPageEntryI>({
    content_type: 'modularPage',
    'fields.slug': slug,
    include: 10,
  })
  const entry = entries.items[0]
  const page = await processModularPage(entry)
  const pageData = populateDefaults(page)

  return {
    props: {
      ...pageData,
    },
  }
}

const populateDefaults = (page: ModularPageI): ModularPageI => {
  const data = page

  if (!data.pageDescription) {
    data.pageDescription =
      'Undock is the fastest way to find time to meet with anyone. Say hello to the super calendar with scheduling that works like autocomplete.'
  }

  if (!data.pageImage) {
    data.pageImage =
      '//images.ctfassets.net/69ygoegobx90/6cjwMv4zEEhPIa6jeNaZBz/7f4e0f3b71646ce8b5091c5ea5d67959/og-image-default.jpg'
  }

  if (!data.pageKeywords) {
    data.pageKeywords = [
      'scheduling',
      'chrome extension',
      'calendar',
      'calendly',
    ]
  }

  if (!data.pageTitle) {
    data.pageTitle = 'Undock'
  }

  return data
}

export default ModularPage
